import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { createRequest, getToolCategories, ToolCategoryInfo } from '../api';

const RequestForm: React.FC = () => {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [scope, setScope] = useState<'read-only' | 'read-write'>('read-only');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Advanced permissions
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [categories, setCategories] = useState<ToolCategoryInfo[]>([]);
  const [catPrefs, setCatPrefs] = useState<Record<string, { read: boolean; write: boolean }>>({});

  useEffect(() => {
    getToolCategories().then(cats => {
      setCategories(cats);
      const defaults: Record<string, { read: boolean; write: boolean }> = {};
      for (const c of cats) defaults[c.id] = { read: true, write: false };
      setCatPrefs(defaults);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!reason.trim()) {
      setError('Please provide a reason for your access request.');
      return;
    }

    setSubmitting(true);
    try {
      await createRequest({
        name: (user?.name || user?.username) ?? '',
        email: user?.email ?? '',
        reason: reason.trim(),
        scope,
        ...(advancedOpen ? { categoryPreferences: catPrefs } : {}),
      });
      setSuccess(true);
      setReason('');
      setScope('read-only');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>Request MCP Access</h1>
      <p style={styles.description}>
        Fill out the form below to request access to the MCP server. An admin will
        review your request and you will be notified of the outcome.
      </p>

      {success ? (
        <div style={styles.successCard}>
          <div style={styles.successIcon}>&#10003;</div>
          <h2 style={styles.successHeading}>Request Submitted</h2>
          <p style={styles.successText}>
            Your access request has been submitted successfully. An admin will
            review it shortly and you will be notified of the outcome.
          </p>
          <a href="/my-requests" style={styles.trackLink}>
            Track your request status
          </a>
        </div>
      ) : (
        <>
      {error && <div style={styles.errorBanner}>{error}</div>}

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.field}>
          <label style={styles.label}>Name</label>
          <input
            type="text"
            value={(user?.name || user?.username) ?? ''}
            disabled
            style={{ ...styles.input, ...styles.disabled }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Email</label>
          <input
            type="email"
            value={user?.email ?? ''}
            disabled
            style={{ ...styles.input, ...styles.disabled }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Reason for Access</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why you need access to the MCP server..."
            rows={5}
            style={styles.textarea}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Access Scope</label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as 'read-only' | 'read-write')}
            style={styles.select}
          >
            <option value="read-only">Read Only</option>
            <option value="read-write">Read &amp; Write</option>
          </select>
          <p style={styles.hint}>
            Read-only allows viewing Jira/Confluence data. Read-write additionally
            allows creating and modifying resources.
          </p>
        </div>

        <div style={styles.field}>
          <button
            type="button"
            onClick={() => setAdvancedOpen(!advancedOpen)}
            style={styles.advancedToggle}
          >
            {advancedOpen ? '- Hide' : '+ Show'} Advanced Permissions
          </button>
          {!advancedOpen && (
            <p style={styles.hint}>Default: Read-only access to all enabled tool categories.</p>
          )}
          {advancedOpen && categories.length > 0 && (
            <div style={styles.advancedPanel}>
              <p style={styles.hint}>
                Select specific tool categories and permission levels. Your administrator will review these preferences.
              </p>
              <table style={styles.catTable}>
                <thead>
                  <tr>
                    <th style={styles.catTh}>Category</th>
                    <th style={styles.catThCenter}>Read</th>
                    <th style={styles.catThCenter}>Write</th>
                    <th style={styles.catThRight}>Tools</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => {
                    const pref = catPrefs[cat.id] || { read: true, write: false };
                    return (
                      <tr key={cat.id} style={styles.catTr}>
                        <td style={styles.catTd}>{cat.displayName}</td>
                        <td style={styles.catTdCenter}>
                          <input
                            type="checkbox"
                            checked={pref.read}
                            onChange={() => {
                              const updated = { ...pref, read: !pref.read };
                              if (!updated.read) updated.write = false;
                              setCatPrefs({ ...catPrefs, [cat.id]: updated });
                            }}
                          />
                        </td>
                        <td style={styles.catTdCenter}>
                          <input
                            type="checkbox"
                            checked={pref.write}
                            onChange={() => {
                              const updated = { ...pref, write: !pref.write };
                              if (updated.write) updated.read = true;
                              setCatPrefs({ ...catPrefs, [cat.id]: updated });
                            }}
                          />
                        </td>
                        <td style={styles.catTdRight}>
                          <span style={styles.catBadge}>{cat.readCount}R</span>
                          <span style={{ ...styles.catBadge, background: '#fef3c7', color: '#92400e' }}>{cat.writeCount}W</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <button type="submit" disabled={submitting} style={styles.submitBtn}>
          {submitting ? 'Submitting...' : 'Submit Request'}
        </button>
      </form>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: 600,
    margin: '0 auto',
  },
  heading: {
    fontSize: '1.6rem',
    fontWeight: 700,
    marginBottom: '0.5rem',
  },
  description: {
    color: '#666',
    marginBottom: '1.5rem',
    lineHeight: 1.6,
  },
  successCard: {
    background: '#fff',
    borderRadius: 8,
    padding: '3rem 2rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    textAlign: 'center',
  },
  successIcon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: '#d4edda',
    color: '#155724',
    fontSize: '1.8rem',
    lineHeight: '56px',
    margin: '0 auto 1rem',
    fontWeight: 700,
  },
  successHeading: {
    fontSize: '1.3rem',
    fontWeight: 700,
    marginBottom: '0.75rem',
  },
  successText: {
    color: '#555',
    fontSize: '0.95rem',
    lineHeight: 1.6,
    marginBottom: '1.5rem',
    maxWidth: 420,
    margin: '0 auto 1.5rem',
  },
  trackLink: {
    display: 'inline-block',
    background: '#4a6cf7',
    color: '#fff',
    textDecoration: 'none',
    padding: '0.6rem 1.5rem',
    borderRadius: 6,
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  errorBanner: {
    background: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: 6,
    padding: '0.85rem 1rem',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
  },
  form: {
    background: '#fff',
    borderRadius: 8,
    padding: '1.5rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  field: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontWeight: 600,
    fontSize: '0.9rem',
    marginBottom: '0.35rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: 4,
    fontSize: '0.9rem',
    outline: 'none',
  },
  disabled: {
    background: '#f0f0f0',
    color: '#888',
    cursor: 'not-allowed',
  },
  textarea: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: 4,
    fontSize: '0.9rem',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: 4,
    fontSize: '0.9rem',
    background: '#fff',
    outline: 'none',
  },
  hint: {
    fontSize: '0.8rem',
    color: '#888',
    marginTop: '0.35rem',
  },
  submitBtn: {
    background: '#4a6cf7',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 2rem',
    borderRadius: 6,
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
  },
  advancedToggle: {
    background: 'none',
    border: 'none',
    color: '#4a6cf7',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    padding: 0,
  },
  advancedPanel: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    background: '#f9fafb',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
  },
  catTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
    marginTop: '0.5rem',
  },
  catTh: { textAlign: 'left' as const, padding: '0.4rem 0.5rem', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
  catThCenter: { textAlign: 'center' as const, padding: '0.4rem 0.5rem', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
  catThRight: { textAlign: 'right' as const, padding: '0.4rem 0.5rem', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
  catTr: { borderBottom: '1px solid #f3f4f6' },
  catTd: { padding: '0.35rem 0.5rem' },
  catTdCenter: { padding: '0.35rem 0.5rem', textAlign: 'center' as const },
  catTdRight: { padding: '0.35rem 0.5rem', textAlign: 'right' as const },
  catBadge: { display: 'inline-block', padding: '1px 5px', borderRadius: 3, background: '#e0f2fe', color: '#0369a1', fontSize: '0.7rem', fontWeight: 600, marginLeft: 3 },
};

export default RequestForm;
